import React from 'react';
import cn from 'classnames';
import { useRecoilValue } from 'recoil';
import { Button } from '@ui-elements';
import { usePortals } from 'react-portal-hook';
import { GfvDisclaimer, PostcodeModal } from 'common/components';
import {
  UserLocationState,
  getFinanceFormInputPersonalised,
} from '~/common/store';
import { CompareRangeVariant, ModelPageUrl } from '~/pages/model-landing/store';
import { CompareRangePriceDetail } from '~/pages/model-landing/components/model-landing-price-info';
import { CompareRangeKeyFeatures } from '~/pages/model-landing/components/model-landing-compare-keyfeatures';
import styles from './index.module.scss';
import { dataLayerPushDownloadBrochure } from '~/pages/model-landing/datalayer/push-download-brochure';
import ImgLazyLoad from '~/common/components/image-lazy-loading';

interface ModelWalkVariantProps extends CompareRangeVariant {
  modelName?: string;
  modelPimIdentifier?: string;
  className?: string;
  hideGfv?: boolean;
}
export const ModelWalkVariantContent = React.memo<ModelWalkVariantProps>(
  ({
    className,
    hideGfv,
    imageSrc,
    seriesName,
    legacyId,
    pimIdentifier,
    ...props
  }) => {
    const portalManager = usePortals();
    const modelUrl = useRecoilValue(ModelPageUrl);
    const locationState = useRecoilValue(UserLocationState);

    const openPostcodeModal = () => {
      portalManager.open((portal) => (
        <PostcodeModal closeModal={portal.close} />
      ));
    };

    const pushToDataLayer = React.useCallback(() => {
      if (props.modelPimIdentifier === 'AUFOR26') {
        dataLayerPushDownloadBrochure('New Forester');
      }
    }, []);

    return (
      <div className={cn(styles.variantContainer, className)}>
        <div className={styles.variantInfoContainer}>
          <div className={styles.variantInfo}>
            <ImgLazyLoad src={imageSrc} alt={seriesName} loading="lazy" />
            <p>{seriesName}</p>
          </div>

          {!props.hidePriceDetails && (
            <>
              <CompareRangePriceDetail
                type="RDP"
                variantId={legacyId}
                modelName={seriesName}
                pimIdentifier={pimIdentifier}
                isFinanceFormInputPersonalised={getFinanceFormInputPersonalised()}
                containerClass={styles.priceDetailsContainer}
                labelClass={styles.priceLabelClass}
                priceClass={styles.priceClass}
                recommendedRdpLabelClass={styles.recommendedRdpLabelClass}
                recommendedRdpLabel="Recommended Driveaway Price"
              />
              {!hideGfv && (
                <CompareRangePriceDetail
                  type="Finance"
                  variantId={legacyId}
                  pimIdentifier={pimIdentifier}
                  isFinanceFormInputPersonalised={getFinanceFormInputPersonalised()}
                  containerClass={styles.financeContainer}
                  labelClass={styles.priceLabelClass}
                  priceClass={styles.priceClass}
                />
              )}
              <div>
                <p className={styles.rdpDisclaimer}>
                  Recommended Driveaway Price (RDP) shown based on{' '}
                  {`${locationState.name}, ${locationState.postcode}`}. RDP may
                  change based on location.{' '}
                  <span
                    onClick={openPostcodeModal}
                    className={styles.postcodeModal}
                  >
                    Enter your postcode
                  </span>{' '}
                  for the RDP in your area.
                </p>

                {!hideGfv && <GfvDisclaimer variantPimId={pimIdentifier} />}
              </div>
            </>
          )}

          <CompareRangeKeyFeatures
            useDots
            defaultDisplayAll
            hideShowFullSpecsButton
            customTitle={
              !!props.keyFeatureDisclaimer && (
                <div
                  className={styles.keyfeatTitle}
                  dangerouslySetInnerHTML={{
                    __html: props.keyFeatureDisclaimer,
                  }}
                />
              )
            }
            containerClass={styles.keyFeatureContainerClass}
            keyFeature={props?.keyFeatures || []}
          />
        </div>

        <div>
          {props?.learnMore && !props?.learnMore?.ctaHidden && (
            <Button
              type={props?.learnMore?.ctaButtonType || 'primary'}
              buttonSize={props?.learnMore?.ctaButtonSize || 'large'}
              buttonWidth={props?.learnMore?.ctaFullWidth || 'full-width'}
              href={props?.learnMore?.ctaUrl?.url || '#'}
            >
              {props?.learnMore?.ctaUrl?.name || `Learn more`}
            </Button>
          )}

          {!props?.buildYourOwn?.ctaHidden && (
            <Button
              type={props?.buildYourOwn?.ctaButtonType || 'primary'}
              buttonSize={props?.buildYourOwn?.ctaButtonSize || 'large'}
              buttonWidth={props?.buildYourOwn?.ctaFullWidth || 'full-width'}
              href={props?.buildYourOwn?.ctaUrl?.url || '/configure'}
            >
              {props?.buildYourOwn?.ctaUrl?.name || `Build your own`}
            </Button>
          )}

          {!props.viewSpecsAndFeat?.ctaHidden && (
            <Button
              type="text"
              target={props.viewSpecsAndFeat?.ctaUrl?.target || '_self'}
              href={props.viewSpecsAndFeat?.ctaUrl?.url || `/${modelUrl}/specs`}
              className={styles.fullSpecsFeatButton}
              onClick={pushToDataLayer}
            >
              {props.viewSpecsAndFeat?.ctaUrl?.name || 'View Specs & Features'}
            </Button>
          )}
        </div>
      </div>
    );
  }
);
